export class ApiError extends Error {
  status: number
  body: string
  code?: string

  constructor(message: string, status: number, body: string, code?: string) {
    super(message)
    this.status = status
    this.body = body
    this.code = code
  }
}

const API_BASE_OVERRIDE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')

function apiBase(): string {
  // Same-origin everywhere: Vite dev proxies /api → :3001, prod Worker serves
  // both SPA and API on the same hostname. Override only used for unusual setups
  // (e.g. SPA on a different host than the API).
  return API_BASE_OVERRIDE
}

function buildUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path
  return `${apiBase()}${path.startsWith('/') ? '' : '/'}${path}`
}

async function request(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers)
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  return fetch(buildUrl(path), {
    ...init,
    headers,
    credentials: 'include',
  })
}

function parseCode(body: string): string | undefined {
  try {
    const parsed = JSON.parse(body) as { code?: string }
    return parsed.code
  } catch {
    return undefined
  }
}

export async function apiJSON<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await request(path, init)
  if (!res.ok) {
    const body = await res.text()
    throw new ApiError(`API ${res.status} on ${path}`, res.status, body, parseCode(body))
  }
  return (await res.json()) as T
}

export async function apiVoid(path: string, init: RequestInit = {}): Promise<void> {
  const res = await request(path, init)
  if (!res.ok) {
    const body = await res.text()
    throw new ApiError(`API ${res.status} on ${path}`, res.status, body, parseCode(body))
  }
}

export async function authedJSON<T>(path: string, init: RequestInit = {}): Promise<T> {
  return apiJSON<T>(path, init)
}

export async function authedVoid(path: string, init: RequestInit = {}): Promise<void> {
  return apiVoid(path, init)
}
